import React, {useMemo, useState} from 'react';
import {createRefetchContainer, graphql, RelayRefetchProp} from 'react-relay';
import styled from 'styled-components';
import {Button, Checkbox, ComboBox, FlexColumn, FlexRow, Link, SubHeading, Text, TextField} from '@forecast-it/design-system';
import {SageSignupControlPage_viewer} from './__generated__/SageSignupControlPage_viewer.graphql';
import MissionControlHeader from './MissionControlHeader';
import {hideLoader, showLoader} from '../../global_loader';
import CreateSageCompanyMutation from '../../../mutations/mission_control/CreateSageCompanyMutation';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {CommitMutation} from '../../../mutations/CommitMutation';
import MissionControlActivateSageIntacctMutation from '../../../mutations/mission_control/MissionControlActivateSageIntacctMutation';
import MissionControlDeactivateSageIntacctMutation from '../../../mutations/mission_control/MissionControlDeactivateSageIntacctMutation';
import {notNullishAndPredicate} from '../../../forecast-app/shared/util/NotNullPredicate';

const SignupForm = styled.div`
	display: flex;
	margin: 24px;
	width: 500px;
`;

const CompanyList = styled.div`
	margin: 24px;
`;

type SageCustomerItem = {
	id: string;
	name: string;
};

interface SageSignupControlPageProps {
	viewer: SageSignupControlPage_viewer;
	relay: RelayRefetchProp;
}

function getSageCustomerItems(viewer: SageSignupControlPage_viewer): SageCustomerItem[] {
	if (!viewer?.company?.unlinkedSageCustomers) {
		return [];
	}
	const items: SageCustomerItem[] = [];
	viewer.company.unlinkedSageCustomers.forEach(customer => {
		if (customer) {
			items.push({id: customer.id, name: customer.name || 'No name'});
		}
	});
	return items;
}

function SageSignupControlPage({viewer, relay}: SageSignupControlPageProps): React.JSX.Element {
	const [companyName, setCompanyName] = useState('');
	const [email, setEmail] = useState('');
	const [emailValid, setEmailValid] = useState(true);
	const [sageCompanyId, setSageCompanyId] = useState(null);
	const [projectFinancialsAccess, setProjectFinancialsAccess] = useState(false);
	const [revenueAccess, setRevenueAccess] = useState(false);
	const [financeMenuAccess, setFinanceMenuAccess] = useState(false);
	const [reportingAccess, setReportingAccess] = useState(false);
	const [timesheetsAccess, setTimesheetsAccess] = useState(false);
	const [loading, setLoading] = useState(false);

	const [showActivate, setShowActive] = useState(false);
	const sagePSACompanies = useMemo(() => {
		const companies = viewer.missionControlData?.sagePSACompanies?.filter(notNullishAndPredicate()) || [];
		companies.sort((a, b) => {
			return a.companyId > b.companyId ? -1 : 1;
		});
		return companies;
	}, [viewer]);

	const companiesWithSageEnabled = useMemo(() => {
		const companies = viewer.missionControlData?.companiesWithSageEnabled?.filter(notNullishAndPredicate()) || [];
		companies.sort((a, b) => {
			return a.companyId > b.companyId ? -1 : 1;
		});
		return companies;
	}, [viewer]);

	function validateEmail(email: string): void {
		const valid = email.trim() === '' || /(.+)@(.+)\.(.+)/.test(email.trim());
		setEmailValid(valid);
		setEmail(email.trim());
	}

	function showError(title: string, messages: string[]) {
		showModal({
			type: MODAL_TYPE.WARNING,
			message: title,
			warningInformation: messages,
			buttons: [
				{
					text: 'Ok',
				},
			],
		});
	}

	function activateInWorkato(companyId: string) {
		showLoader();
		setLoading(true);
		CommitMutation(MissionControlActivateSageIntacctMutation, {companyId}, response => {
			hideLoader();
			setLoading(false);
			const outcome = response.MissionControlActivateSageIntacct?.outcome;
			if (outcome === 'Success') {
				relay.refetch({});
				createToast({duration: 5000, message: 'Company Successfully activated!'});
			} else {
				showError('Error activating company', [
					'Could not activate company in Workato. Please retry activation!',
					response.MissionControlActivateSageIntacct?.errorMessage || '',
				]);
			}
		});
	}

	function deactivateInWorkato(companyId: string) {
		showLoader();
		setLoading(true);
		CommitMutation(MissionControlDeactivateSageIntacctMutation, {companyId}, response => {
			hideLoader();
			setLoading(false);
			const outcome = response.missionControlDeactivateSageIntacct?.outcome;
			if (outcome === 'Success') {
				relay.refetch({});
				createToast({duration: 5000, message: 'Company Successfully deactivated!'});
			} else {
				showError('Error deactivating company', [
					'Could not deactivate company in Workato. Please retry deactivation!',
					response.missionControlDeactivateSageIntacct?.errorMessage || '',
				]);
			}
		});
	}

	function submit(): void {
		if (loading) {
			return;
		}
		showLoader();
		setLoading(true);

		const domain = email.split('@')[1].split('.')[0];
		const accountName = domain.replace(/^./, domain[0].toUpperCase());

		CommitMutation(
			CreateSageCompanyMutation,
			{
				email,
				companyName: companyName || accountName[0].toUpperCase() + accountName.slice(1).toLowerCase(),
				unlinkedSageCustomerID: sageCompanyId,
				projectFinancialsAccess,
				revenueAccess,
				financeMenuAccess,
				reportingAccess,
				timesheetsAccess,
			},
			response => {
				// Check response
				let errorMessage;
				const resultCode = response.CreateSageCompany?.resultCode;
				switch (resultCode) {
					case '0':
						// Success
						createToast({
							duration: 10000,
							message: 'Company Successfully created! Activating in Workato...',
						});
						activateInWorkato(response.CreateSageCompany?.companyId || '');
						relay.refetch({});
						break;
					case '1':
						// Email is missing...
						errorMessage = `Create company failed with resultCode: ${resultCode}. Could not find email`;
						break;
					case '2':
						// Email in use
						errorMessage = `Create company failed with resultCode: ${resultCode}. Email is already in use.`;
						break;
					default:
						// Other error. Contact a developer...
						errorMessage = `Create company failed with resultCode: ${resultCode}. Contact a developer to find out what it means...`;
				}
				if (errorMessage) {
					hideLoader();
					setLoading(false);
					showError('Error creating company', [errorMessage]);
				}
			}
		);
	}

	return (
		<div>
			<MissionControlHeader viewer={viewer} />
			<SignupForm>
				<FlexColumn>
					<SubHeading>Sage PSA Signup</SubHeading>
					<Text>Here you can create a new Forecast company with the Sage module enabled.</Text>
					<Text>Sage admin will receive an email with further instructions to complete the signup.</Text>
					<ComboBox
						label="Sage Company"
						value={sageCompanyId}
						items={getSageCustomerItems(viewer)}
						onChange={setSageCompanyId}
					>
						{(sageCompany: SageCustomerItem) => (
							<ComboBox.Item textValue={sageCompany.name}>
								<Text>{sageCompany.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
					<TextField
						label="Company Name"
						value={companyName}
						onValueChange={val => {
							setCompanyName(val);
						}}
						placeholder="Write company name here..."
					/>
					<FlexRow alignItems="end">
						<TextField
							label="Email"
							value={email}
							onChange={e => {
								validateEmail(e.target.value);
							}}
							placeholder="Write Sage admin email here..."
						/>
					</FlexRow>
					<SubHeading>Financial Configuration Options</SubHeading>
					<Text color={Text.Color.auxiliary}>These can be changed later from the Modules control panel page.</Text>
					<Checkbox
						labelText={'Project Financials on'}
						checked={projectFinancialsAccess}
						onChange={() => setProjectFinancialsAccess(!projectFinancialsAccess)}
					></Checkbox>
					<Checkbox
						labelText={'Revenue on'}
						checked={revenueAccess}
						onChange={() => setRevenueAccess(!revenueAccess)}
						disabled={!projectFinancialsAccess}
					></Checkbox>
					<Checkbox
						labelText={'Finance menu on'}
						checked={financeMenuAccess}
						onChange={() => setFinanceMenuAccess(!financeMenuAccess)}
						disabled={!projectFinancialsAccess}
					></Checkbox>
					<br />
					<SubHeading>Other configuration options</SubHeading>
					<Checkbox
						labelText={'Reporting in Sage PSA'}
						checked={reportingAccess}
						onChange={() => setReportingAccess(!reportingAccess)}
					></Checkbox>
					<Text color={Text.Color.auxiliary}>Timesheets in Sage PSA can only be set once so please be careful!</Text>
					<Checkbox
						labelText={'Timesheets in Sage PSA'}
						checked={timesheetsAccess}
						onChange={() => setTimesheetsAccess(!timesheetsAccess)}
					></Checkbox>
					<br />
					<Button
						disabled={!email || !emailValid || loading || !sageCompanyId}
						onPress={() => {
							submit();
						}}
						noMargin
					>
						Signup
					</Button>
				</FlexColumn>
			</SignupForm>
			<CompanyList className="mission-control-list">
				<SubHeading>Sage PSA Companies {!showActivate && '(missing activation)'}</SubHeading>
				<div>
					<button onClick={() => setShowActive(!showActivate)}>{showActivate ? 'Hide active' : 'Show active'}</button>
				</div>
				<table>
					<thead>
						<tr>
							<th>ID</th>
							<th>Name</th>
							<th>Created At</th>
							<th>Activated</th>
							<th>Workato Customer ID</th>
							<th>Activate</th>
							<th>Deactivate</th>
						</tr>
					</thead>
					<tbody>
						{sagePSACompanies
							.filter(company => showActivate || !company.activated)
							.map(company => (
								<tr>
									<td>{company.companyId}</td>
									<td>{company.name}</td>
									<td>{company.createdAt}</td>
									<td>{company.activated ? '✓' : ''}</td>
									<td>{company.workatoCustomerId}</td>
									<td>
										<Button
											disabled={!!company.activated}
											onPress={() => {
												activateInWorkato(company.id);
											}}
											noMargin
										>
											Activate
										</Button>
									</td>
									<td>
										<Button
											disabled={!company.activated}
											onPress={() => {
												deactivateInWorkato(company.id);
											}}
											noMargin
										>
											Deactivate
										</Button>
									</td>
								</tr>
							))}
					</tbody>
				</table>
			</CompanyList>
			<CompanyList className="mission-control-list">
				<SubHeading>Forcast companies with Sage active</SubHeading>
				<table>
					<thead>
						<tr>
							<th>ID</th>
							<th>Name</th>
							<th>Created At</th>
							<th>Activated</th>
							<th>Workato Customer ID</th>
							<th>Sage Sync Analysis</th>
							<th>Deactivate</th>
						</tr>
					</thead>
					<tbody>
						{companiesWithSageEnabled.map(company => (
							<tr>
								<td>{company.companyId}</td>
								<td>{company.name}</td>
								<td>{company.createdAt}</td>
								<td>{company.activated ? '✓' : ''}</td>
								<td>{company.workatoCustomerId}</td>
								<td>
									<Link ellipsis href={'/control/sage-sync-analysis/' + company.companyId}>
										View
									</Link>
								</td>
								<td>
									<Button
										disabled={!company.activated}
										onClick={() => {
											deactivateInWorkato(company.id);
										}}
										noMargin
									>
										Deactivate
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</CompanyList>
		</div>
	);
}

const sageSignupQuery = graphql`
	query SageSignupControlPage_Query {
		viewer {
			actualPersonId
			component(name: "sage_signup")
			...SageSignupControlPage_viewer
		}
	}
`;

export {sageSignupQuery};

export default createRefetchContainer(
	SageSignupControlPage,
	{
		viewer: graphql`
			fragment SageSignupControlPage_viewer on Viewer {
				id
				...MissionControlHeader_viewer
				company {
					id
					unlinkedSageCustomers {
						id
						name
					}
				}
				missionControlData {
					sagePSACompanies {
						id
						companyId
						name
						activated
						workatoCustomerId
						createdAt
					}
					companiesWithSageEnabled {
						id
						companyId
						name
						activated
						workatoCustomerId
						createdAt
					}
				}
			}
		`,
	},
	graphql`
		query SageSignupControlPageRefetchQuery {
			viewer {
				...SageSignupControlPage_viewer
			}
		}
	`
);
